import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { GoalConfig, Pin } from "../../../shared/goals/config";
import { CHANNEL_ORDER, CHANNELS } from "../../../shared/goals/channels";
import { sectionId, splitSections } from "../../../shared/goals/library";
import { useNative } from "../../../shared/phone/media";
import { Chevron } from "../../../shared/phone/Row";
import { Sheet } from "../../../shared/phone/Sheet";
import { Icon } from "../../components/ui/Icon";
import { Orb } from "../../components/ui/Orb";
import { ApiError } from "../../lib/api";
import { goalsApi } from "../../lib/goalsApi";
import { libraryApi, type LibraryItem } from "../../lib/libraryApi";
import { runActive, type RunInfo } from "../../lib/productsApi";
import type { SavePatch } from "./ChannelsStep";
import { DocWalk, type WalkPart } from "./DocWalk";
import { WizardNav, type ProductRef } from "./FlowHead";
import { InPlaceTip } from "./InPlace";
import { Spin } from "./Native";
import { PlaybookPart } from "./pbShape";
import { buildLog } from "./runEvents";
import { useRun } from "./useRun";

interface Props {
  goalId: string;
  product: ProductRef;
  config: GoalConfig;
  /** The run drafting the playbook (in the address, so a reload keeps waiting for it). */
  runId: string | null;
  onRun: (runId: string | null) => void;
  save: SavePatch;
  /** A note to the owner; `false` marks a failure, which must not look like a success. */
  say: (msg: string, ok?: boolean) => void;
  onBack: () => void;
  onNext: () => void;
  /** The next step's button, and whether it is working. */
  next: string;
  busy: boolean;
  /** On a phone and an iPad: the step's heading. */
  head?: ReactNode;
}

interface DraftSection {
  id?: string;
  title?: string;
  source?: string;
  why?: string;
}

function aboutOf(item: LibraryItem): string {
  const { intro } = splitSections(item.body);
  return intro
    .split("\n")
    .filter((l) => !/^#\s/.test(l))
    .join("\n")
    .trim();
}

/** The playbook the run saved, read from the run's own result. */
async function savedBy(run: RunInfo): Promise<LibraryItem | null> {
  let id: unknown = null;
  try {
    id = (JSON.parse(run.summary_json ?? "null") as { result?: { tool?: string; id?: unknown } } | null)?.result?.id;
  } catch {
    id = null;
  }
  return typeof id === "string" ? (await libraryApi.item(id)).item : null;
}

/** A part's first sentence, for its row: list marks and bold left out. */
function firstLine(text: string): string {
  const line =
    text
      .split("\n")
      .map((l) => l.replace(/^\s*(?:[-*]|\d+\.)\s+/, "").replace(/\*\*/g, "").trim())
      .find((l) => l && !l.endsWith(":")) ?? "";
  return line.match(/^.+?[.!?](?=\s|$)/)?.[0] ?? line;
}

function Busy({ title, lines }: { title: string; lines: string[] }) {
  return (
    <section className="card">
      <div className="busy">
        <div className="orb-tile">
          <Orb live />
        </div>
        <h3>{title}</h3>
        <div className="dsteps">
          {lines.map((l, i) => (
            <div className="dstep" key={i}>
              <Icon name="check" />
              {l}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Step 3, the playbook: how this goal markets on its channel. The agent drafts the goal's own playbook from the
 * product's profile, the channel's method and the goal's numbers as soon as the step opens; the owner edits any
 * part, and the edits stay with this goal. Next drafts the plan from it.
 */
export function PlaybookStep({ goalId, product, config, runId, onRun, save, say, onBack, onNext, next, busy, head }: Props) {
  const native = useNative();
  const [openPart, setOpenPart] = useState<number | null>(null);
  const pin = config.library.find((p) => p.kind === "playbook") ?? null;
  const [pinned, setPinned] = useState<LibraryItem | null>(null);
  const [missing, setMissing] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  // Why: a changed line shows at once while its save is on the way; the goal's saved words take over after.
  const [pending, setPending] = useState<Record<string, string>>({});
  const { run, events } = useRun(runId);
  const pinnedRun = useRef<string | null>(null);

  const fail = useCallback((e: unknown) => say(e instanceof ApiError ? e.message : "Something went wrong. Try again.", false), [say]);

  const pinItem = pin?.itemId ?? null;
  useEffect(() => {
    if (!pinItem) return;
    let live = true;
    libraryApi.item(pinItem).then(
      (r) => live && setPinned(r.item),
      (e) => live && fail(e),
    );
    return () => {
      live = false;
    };
  }, [pinItem, fail]);

  const start = useCallback(async () => {
    setMissing(null);
    setStarting(true);
    try {
      onRun((await goalsApi.playbook(goalId)).runId);
    } catch (e) {
      fail(e);
    } finally {
      setStarting(false);
    }
  }, [goalId, onRun, fail]);

  // Why: every goal gets its own playbook; one made before that pins another's, which it replaces with its own.
  const needsDraft = !pin || (!!pinned && (pinned.meta as { goalId?: string }).goalId !== goalId);
  const asked = useRef(false);
  useEffect(() => {
    if (!needsDraft || runId || asked.current) return;
    asked.current = true;
    void start();
  }, [needsDraft, runId, start]);

  // Why: the draft is read from the library once the run that writes it has ended, then pinned to the goal so edits are kept.
  const ended = run && run.id === runId && !runActive(run.state) ? run : null;
  useEffect(() => {
    if (!ended || !needsDraft || pinnedRun.current === ended.id) return;
    if (ended.state !== "done") {
      setMissing(ended.error || "The agent stopped before it saved a playbook.");
      return;
    }
    let live = true;
    savedBy(ended).then(
      async (item) => {
        if (!live) return;
        if (!item) {
          setMissing("The agent finished without saving a playbook.");
          return;
        }
        pinnedRun.current = ended.id;
        const mine: Pin = { kind: "playbook", name: item.name, version: item.version, itemId: item.id, edits: {} };
        if (await save({ library: [mine, ...config.library.filter((p) => p.kind !== "playbook")] }, "")) {
          onRun(null);
          say("Draft ready. Edit any part, or keep it as it is.");
        }
      },
      (e) => live && fail(e),
    );
    return () => {
      live = false;
    };
  }, [ended, needsDraft, config.library, save, onRun, say, fail]);

  const edits: Record<string, string> = pin?.edits ?? {};
  const running = starting || (needsDraft && !missing);
  const showing = needsDraft ? null : pinned;

  const setEdit = async (id: string, text: string | null, original: string) => {
    if (!pin) return;
    const after = { ...edits };
    if (text === null || text.trim() === original.trim()) delete after[id];
    else after[id] = text;
    if (JSON.stringify(after) === JSON.stringify(edits)) return;
    const library: Pin[] = config.library.map((p) => (p === pin ? { ...p, edits: after } : p));
    if (text !== null) setPending((x) => ({ ...x, [id]: text }));
    await save({ library }, text === null ? "Back to the original." : "Saved.");
    setPending((x) => {
      const rest = { ...x };
      delete rest[id];
      return rest;
    });
  };

  const walk = useMemo(() => {
    if (!showing) return null;
    const { sections } = splitSections(showing.body);
    const meta = showing.meta as { sections?: DraftSection[] };
    const drafts = new Map((meta.sections ?? []).map((s) => [sectionId(s.title ?? s.id ?? ""), s]));
    const byId = new Map(sections.map((s) => [s.id, s]));
    const parts: (WalkPart & { source?: string })[] = sections.map((s) => ({ id: s.id, title: s.title, why: drafts.get(s.id)?.why, source: drafts.get(s.id)?.source }));
    return { parts, byId };
  }, [showing]);

  const channel = CHANNEL_ORDER.find((k) => config.channels[k]?.on);
  const channelLabel = channel ? CHANNELS[channel].label : "";
  /** Where a part came from, as the draft says. */
  const source: Record<string, [string, string]> = {
    profile: ["tailored", `From ${product.name}'s profile`],
    method: ["default", `From the ${channelLabel} skill`],
    goal: ["goal", "Fitted to this goal"],
    default: ["default", "Flashadz default"],
    tailored: ["tailored", `Tailored for ${product.name}`],
    lesson: ["lesson", "From a lesson"],
  };

  let body = null;
  if (missing) {
    body = (
      <section className="card">
        <div className="busy">
          <div className="orb-tile">
            <Orb live={false} />
          </div>
          <h3>The draft is not ready</h3>
          <p className="fine">{missing}</p>
          <div className="m-act">
            <button type="button" className="btn sm" disabled={starting} onClick={() => void start()}>
              Try again
            </button>
          </div>
        </div>
      </section>
    );
  } else if (running) {
    const lines = buildLog(run, events)
      .filter((e) => e.tool)
      .map((e) => e.x)
      .filter((x, i, all) => all[i - 1] !== x)
      .slice(-5);
    body = <Busy title="Drafting the playbook for this goal" lines={lines} />;
  } else if (showing && walk) {
    const badge = channelLabel || "Draft";
    const about = aboutOf(showing);
    body = (
      <div id="dwWz">
        <DocWalk
          key={showing.id}
          inline
          nav={false}
          parts={walk.parts}
          head={{ title: "This goal's playbook", ver: `v${showing.version}`, badge, sub: about || undefined }}
          edited={(p) => edits[p.id] !== undefined}
          marker={(p) => {
            if (edits[p.id] !== undefined)
              return (
                <span className="chip rl">
                  <Icon name="pen" />
                  Your edits
                </span>
              );
            const src = source[(p as { source?: string }).source ?? ""];
            return src ? <span className={`src ${src[0]}`}>{src[1]}</span> : null;
          }}
          body={(p) => {
            const original = walk.byId.get(p.id)?.text ?? "";
            const text = pending[p.id] ?? edits[p.id] ?? original;
            return (
              <>
                <InPlaceTip />
                <div className="dw-body" role="group" aria-label={p.title}>
                  <PlaybookPart id={p.id} text={text} edit={{ original, onChange: (t) => void setEdit(p.id, t, original) }} />
                </div>
                {edits[p.id] !== undefined && (
                  <div className="dw-tools">
                    <button type="button" className="wk-undo" onClick={() => void setEdit(p.id, null, original)}>
                      Undo my edit
                    </button>
                  </div>
                )}
              </>
            );
          }}
        />
      </div>
    );
  }

  const ready = !busy && !running && !missing && !!showing;

  if (native) {
    let content: ReactNode = null;
    if (missing)
      content = (
        <div className="pn-card su-state">
          <h3>The draft is not ready</h3>
          <p>{missing}</p>
          <button type="button" className="pn-btn sm soft" disabled={starting} onClick={() => void start()}>
            Try again
          </button>
        </div>
      );
    else if (running)
      content = (
        <section>
          <div className="pn-cap">Drafting the playbook for this goal</div>
          <div className="pn-grp">
            {buildLog(run, events)
              .filter((e) => e.tool)
              .map((e) => e.x)
              .filter((x, i, all) => all[i - 1] !== x)
              .slice(-4)
              .map((l, i) => (
                <div className="pn-row static" key={i}>
                  <span className="su-pst ok">
                    <Icon name="check" />
                  </span>
                  <span className="m">
                    <span className="t">{l}</span>
                  </span>
                </div>
              ))}
            <div className="pn-row static">
              <span className="su-pst">
                <Spin />
              </span>
              <span className="m">
                <span className="t">Writing it for this goal</span>
              </span>
            </div>
          </div>
        </section>
      );
    else if (showing && walk) {
      const about = aboutOf(showing);
      content = (
        <>
          <section>
            <div className="pn-grp">
              <div className="pn-row static su-tall">
                <span className="pn-ico violet">
                  <Icon name="book" />
                </span>
                <span className="m">
                  <span className="t">{showing.title}</span>
                  <span className="s">Version {showing.version}, fitted to this goal</span>
                </span>
              </div>
            </div>
            {about && <p className="pn-note">{about}</p>}
          </section>
          <section>
            <div className="pn-cap">{walk.parts.length === 1 ? "1 part" : `${walk.parts.length} parts`}</div>
            <div className="pn-grp">
              {walk.parts.map((p, k) => (
                <button type="button" className="pn-row su-part" key={p.id} onClick={() => setOpenPart(k)}>
                  <span className="su-n">{k + 1}</span>
                  <span className="m">
                    <span className="t">{p.title}</span>
                    <span className="s">{firstLine(pending[p.id] ?? edits[p.id] ?? walk.byId.get(p.id)?.text ?? "")}</span>
                  </span>
                  {edits[p.id] !== undefined && <span className="pn-pill violet">Your edits</span>}
                  <Chevron />
                </button>
              ))}
            </div>
          </section>
        </>
      );
    }
    const part = walk && openPart !== null ? walk.parts[openPart] : null;
    const after = walk && openPart !== null ? walk.parts[openPart + 1] : undefined;
    const original = part && walk ? (walk.byId.get(part.id)?.text ?? "") : "";
    const close = () => setOpenPart(null);
    const src = part ? source[part.source ?? ""] : undefined;
    return (
      <>
        <div className="pn-stack su-step">
          {head}
          {content}
        </div>
        <Sheet
          open={!!part}
          onClose={close}
          tall
          title={part?.title}
          left={
            <button type="button" className="pn-tbtn strong" onClick={close}>
              Done
            </button>
          }
          right={part && walk ? <span className="su-of">{`${(openPart ?? 0) + 1} of ${walk.parts.length}`}</span> : null}
          foot={
            after ? (
              <button type="button" className="pn-btn pri wide" onClick={() => setOpenPart((k) => (k ?? 0) + 1)}>
                Next: {after.title}
                <Icon name="right" />
              </button>
            ) : (
              <button type="button" className="pn-btn wide" onClick={close}>
                Done
              </button>
            )
          }
        >
          {part && (
            <div className="su-pb" key={part.id}>
              {(src || part.why) && (
                <p className="su-pbsrc">
                  {edits[part.id] !== undefined ? "Your edits" : src?.[1]}
                  {part.why ? <span>{part.why}</span> : null}
                </p>
              )}
              <InPlaceTip />
              <div className="su-pbbody" role="group" aria-label={part.title}>
                <PlaybookPart id={part.id} text={pending[part.id] ?? edits[part.id] ?? original} edit={{ original, onChange: (t) => void setEdit(part.id, t, original) }} />
              </div>
              {edits[part.id] !== undefined && (
                <button type="button" className="pn-btn sm ghost su-undo" onClick={() => void setEdit(part.id, null, original)}>
                  Undo my edit
                </button>
              )}
            </div>
          )}
        </Sheet>
        <WizardNav next={next} ok={ready} busy={busy} onNext={onNext} />
      </>
    );
  }

  return (
    <>
      <div id="pbBody">{body}</div>
      <WizardNav onBack={onBack} next={next} ok={ready} busy={busy} hint="" onNext={onNext} />
    </>
  );
}
